import { SnwButtonType } from "@@/components/Button/buttonProps";
import SnwButtonContainer from "@@/components/Button/SnwButtonContainer";
import RichText from "@@/components/RichText";
import UnorderedList, { ListItemProp } from "@@/components/UnorderedList";
import { BackgroundColor, colorMapper } from "@@/containers/Cards/service-description";
import Box from "@@/elements/Box";
import Typhography from "@@/elements/Typography";
import OptimizelyBlock, { OptimizelyBlockProps } from "@@/integrations/optimizly/Block";
import EpiProperty from "@@/integrations/optimizly/Property";
import cn from "classnames";
import styles from "./index.module.scss";

export type ServiceDescriptionCardProperties = {
  cardLabel: string;
  tag: string;
  cardHeader: string;
  body: string;
};

export type ServiceDescriptionOwnProps = {
  buttons?: SnwButtonType[];
  backgroundColor: BackgroundColor;
  listItems: ListItemProp[];
  showListItemsText: string;
  hideListItemsText: string;
  collapsListItems?: boolean;
  anchorId?: string;
};

export type ServiceDescriptionCardProps = ServiceDescriptionCardProperties &
ServiceDescriptionOwnProps;

const ServiceDescriptionCard = ({
  backgroundColor,
  cardHeader,
  tag,
  cardLabel,
  body,
  buttons,
  listItems,
  collapsListItems,
  showListItemsText,
  hideListItemsText,
  anchorId,
}: ServiceDescriptionCardProps) => {

  const cardClassNames = cn(styles.card);

  return (
    <Box
      id={anchorId}
      width="full"
      pt={10}
      px={8}
      pb={8}
      backgroundColor={colorMapper(backgroundColor)}
      className={cardClassNames}
    >
      {tag && (
        <Box
          className={styles.card_tag}
          px={3}
          py={1}
          backgroundColor="PrimaryGreenTint01">
          <Typhography
            variant="link"
            color="White">{tag}</Typhography>
        </Box>
      )}
      <Box className={styles.text_container}>
        <EpiProperty<ServiceDescriptionCardProperties> name="cardHeader">
          <Typhography
            mb={4}
            asElement="h3"
            variant="heading2"
          >
            {cardHeader}
          </Typhography>
        </EpiProperty>
         {cardLabel && (
          <EpiProperty<ServiceDescriptionCardProperties> name="cardLabel">
            <Typhography
              mb={4}
              asElement="h4"
              variant="heading6"
              color="PrimaryGreen"
            >
             {cardLabel}
            </Typhography>
          </EpiProperty>
        )}
        <Box mb={6}>
          <EpiProperty<ServiceDescriptionCardProperties> name="body">
            <Box>
              <RichText body={body} />
            </Box>
          </EpiProperty>
        </Box>
      </Box>
      <Box>
        {listItems && listItems.length > 0 && (
          <Box mb={buttons && buttons.length > 0 ? 6 : 0}>
            <hr />
            <UnorderedList
              listItems={listItems}
              textVariant="body"
              collapse={collapsListItems}
              showText={showListItemsText}
              hideText={hideListItemsText}
            />
          </Box>
        )}
      </Box>
      <Box
        className={styles.button_wrapper}
        gap={3}
        width="full"
        flexWrap="wrap"
        justifyContent={
          buttons && buttons.length === 1 ? "flex-start" : "space-between"
        }
        alignItems="flex-end"
      >
        {buttons && (
          <SnwButtonContainer buttons={buttons} />
        )}
      </Box>
    </Box>
  );
};

export type ProvidedServiceDescriptionCardProps =
  OptimizelyBlockProps<ServiceDescriptionCardProps> & ServiceDescriptionCardProps;

const ProvidedTextMediaBlockCard = ({
  blockId,
  inEditMode,
  properties,
  anchorId,
  ...rest
}: ProvidedServiceDescriptionCardProps) => (
  <OptimizelyBlock
    blockId={blockId}
    inEditMode={inEditMode}
    properties={properties}
  >
    {(optimizlyProperties) => (
      <ServiceDescriptionCard
        anchorId={anchorId}
        {...rest}
        {...optimizlyProperties} />
    )}
  </OptimizelyBlock>
);

export default ProvidedTextMediaBlockCard;
